import React from 'react';
import { Modal, Button } from 'react-bootstrap';

/**
 * XModals Component
 *
 * Holds all the modals for "X" (formerly Twitter):
 *   1) Connect X Modal
 *   2) Delete (Edit) X Modal
 *   3) Confirm Deletion
 *
 * Props (passed from Accounts.js):
 * ----------------------------------------------------------------
 * showXModal                 : bool       // open/close the Connect X modal
 * setShowXModal              : function   // set state for showXModal
 * xAccounts                  : array      // pending X accounts
 * selectedXIds               : array      // IDs for selected X accounts
 * handleXCheckboxChange      : function   // toggles selected X IDs
 * handleConnectSelectedX     : function   // finalizes "Connect" action
 *
 * showXDeleteModal           : bool       // open/close the Delete X modal
 * setShowXDeleteModal        : function
 * showXDeleteConfirmModal    : bool       // open/close confirm deletion
 * setShowXDeleteConfirmModal : function
 * handleDeleteXAccounts      : function   // called after selecting accounts
 * handleDeleteXConfirm       : function   // final "Yes, Delete" click
 *
 * accounts                   : array      // the unified 'accounts' from Accounts.js
 */

export default function XModals({
  // CONNECT MODAL
  showXModal,
  setShowXModal,
  xAccounts,
  selectedXIds,
  handleXCheckboxChange,
  handleConnectSelectedX,

  // DELETE MODAL
  showXDeleteModal,
  setShowXDeleteModal,
  showXDeleteConfirmModal,
  setShowXDeleteConfirmModal,
  handleDeleteXAccounts,
  handleDeleteXConfirm,

  // All connected accounts from Accounts.js
  accounts
}) {
  // We pull out the 'X' account object so we can list any connected X accounts
  const xAcc = accounts
    ? accounts.find((acc) => acc.platform.toLowerCase() === 'x')
    : null;

  return (
    <>
      {/* ===================== CONNECT X (Twitter) MODAL ===================== */}
      <Modal show={showXModal} onHide={() => setShowXModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Select X Accounts</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {/* If xAccounts is empty, we can display a quick message */}
          {(!xAccounts || xAccounts.length === 0) ? (
            <p>No X accounts found.</p>
          ) : (
            <ul className="list-group">
              {xAccounts.map((acc) => {
                // Always use acc.twitterId (no fallback)
                const tid = acc.twitterId;
                // Check whether it's in the "selectedXIds" array
                const checked = selectedXIds.includes(tid);

                return (
                  <li key={tid} className="list-group-item d-flex align-items-center">
                    <input
                      type="checkbox"
                      className="form-check-input me-2"
                      checked={checked}
                      onChange={() => handleXCheckboxChange(tid)}
                    />
                    {acc.profileImage && (
                      <img
                        src={acc.profileImage}
                        alt={acc.displayName}
                        style={{
                          width: '30px',
                          height: '30px',
                          borderRadius: '50%',
                          marginRight: '8px'
                        }}
                      />
                    )}
                    <span>{acc.displayName}</span>
                  </li>
                );
              })}
            </ul>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowXModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleConnectSelectedX}>
            Connect
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ===================== EDIT/DELETE X MODAL ===================== */}
      <Modal show={showXDeleteModal} onHide={() => setShowXDeleteModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Delete X Accounts</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {/* If xAcc or xAcc.connectedPages is empty, show a quick message */}
          {(!xAcc || !xAcc.connectedPages || xAcc.connectedPages.length === 0) ? (
            <p>No connected X accounts to delete.</p>
          ) : (
            <ul className="list-group">
              {xAcc.connectedPages.map((item) => {
                // For each connected X account, rely on item.twitterId
                const tid = item.twitterId;
                const checked = selectedXIds.includes(tid);

                return (
                  <li key={tid} className="list-group-item d-flex align-items-center">
                    <input
                      type="checkbox"
                      className="form-check-input me-2"
                      checked={checked}
                      onChange={() => handleXCheckboxChange(tid)}
                    />
                    {item.profileImage && (
                      <img
                        src={item.profileImage}
                        alt={item.displayName}
                        style={{
                          width: '30px',
                          height: '30px',
                          borderRadius: '50%',
                          marginRight: '8px'
                        }}
                      />
                    )}
                    <span>{item.displayName}</span>
                  </li>
                );
              })}
            </ul>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowXDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDeleteXAccounts}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ===================== CONFIRM DELETE X MODAL ===================== */}
      <Modal
        show={showXDeleteConfirmModal}
        onHide={() => setShowXDeleteConfirmModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Deletion</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete these X accounts?</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowXDeleteConfirmModal(false)}>
            No
          </Button>
          <Button variant="danger" onClick={handleDeleteXConfirm}>
            Yes, Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
